import gsap from 'gsap';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Icon, Label, Button, Tooltip, ProgressBar } from 'mw-style-react';
import { useIntl, useDebouncedEffect } from 'hooks';
import { HOT_KEYS } from 'constants';
import JSONEditorReact from '@control-front-end/common/components/JSONEditorReact';
import AceEditorReact from '@control-front-end/common/components/AceEditorReact';
import AppUtils from '@control-front-end/utils/utils';
import { makeGetScriptFile } from '../../../../selectors';
import pageConfig from '../../../../shemas/pageConfig.json';
import mes from './intl';
import './ScriptFileEditor.scss';

/**
 * File editor in the script structure
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function ScriptFileEditor(props) {
  const { fileId, readOnly, showPanel, handleTogglePanel, handleChange } =
    props;
  const t = useIntl();
  const saveBtnRef = useRef();
  const file = useSelector((state) => makeGetScriptFile(state, props));
  const [json, setJson] = useState({});
  const [source, setSource] = useState(file.source);
  const [fileChanged, setFileChanged] = useState(new Date().getTime());
  const [savingProgress, setSavingProgress] = useState(null);
  const [errors, setErrors] = useState([]);
  const debouncedSaving = useDebouncedEffect(fileChanged, 5000);
  const editorRef = useRef();
  const isChangedRef = useRef();
  const isJsonFile = file.type === 'application/json';
  const isConfigFile = isJsonFile && file.title === 'config';

  useEffect(() => {
    if (isJsonFile) setJson(JSON.parse(file.source));
  }, [fileId]);

  const saveChanges = () => {
    if (readOnly || !isChangedRef.current) return;
    setSavingProgress('inProgress');
    let newSource;
    if (isJsonFile) {
      const newJson = editorRef.current.get();
      newSource = JSON.stringify(newJson);
    } else {
      newSource = editorRef.current.getValue();
    }
    handleChange({
      obj: { ...file, source: newSource },
      callback: () => {
        isChangedRef.current = false;
        setSavingProgress('success');
      },
    });
  };

  useEffect(() => {
    if (!isChangedRef.current) return;
    return () => {
      // save on close
      try {
        saveChanges();
      } catch (e) {
        console.log(e); // eslint-disable-line
      }
    };
  }, [isChangedRef.current]);

  useEffect(() => {
    if (isJsonFile && errors.length) return;
    // auto-save every 10 seconds
    saveChanges();
  }, [debouncedSaving, errors]);

  /**
   * Processing hot keys for saving
   */
  const handleHotKeys = (e) => {
    if (AppUtils.isCtrlOrMetaKey(e) && e.keyCode === HOT_KEYS.KeyS) {
      e.preventDefault();
      saveChanges();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleHotKeys);
    return () => {
      document.removeEventListener('keydown', handleHotKeys);
    };
  }, []);

  const handleChangeJson = () => {
    if (readOnly) return;
    const newJson = editorRef.current.get();
    isChangedRef.current = true;
    setSource(JSON.stringify(newJson));
    setFileChanged(new Date().getTime());
    setSavingProgress(null);
  };

  const handleChangeText = (text) => {
    if (readOnly) return;
    isChangedRef.current = true;
    setSource(text);
    setFileChanged(new Date().getTime());
    setSavingProgress(null);
  };

  /**
   * File saving progress
   */
  const renderSavingProgress = () => {
    if (!savingProgress || readOnly) return null;
    const onSaving = savingProgress === 'inProgress';
    return (
      <div styleName="sfj__header__progress">
        {onSaving ? (
          <ProgressBar
            styleName="sfj__header__progress__loader"
            type="circle"
            size="small"
          />
        ) : (
          <Icon type="check" color="#99D31B" />
        )}
        <Label value={onSaving ? t(mes.fileOnSaving) : t(mes.fileSaved)} />
      </div>
    );
  };

  /**
   * The good way is to render this refresh button when it's belongs, which requires
   * refactoring and moving refresh functionality on the closest common ancestor level
   */
  const renderRefreshButton = useCallback(
    () =>
      showPanel
        ? ReactDOM.createPortal(
            <div styleName="sfj__header__refresh">
              <Tooltip value={t(mes.pageRefresh)}>
                <div ref={saveBtnRef}>
                  <Button
                    styleName="sfj__header__refreshButton"
                    icon="repeat"
                    type="tertiary"
                    rounded={true}
                    onClick={() => {
                      gsap.to(saveBtnRef.current, { rotation: '+=360' });
                      saveChanges();
                    }}
                  />
                </div>
              </Tooltip>
            </div>,
            document.getElementById('mainRoot')
          )
        : null,
    [showPanel]
  );

  return (
    <div styleName="sfj">
      <div styleName="sfj__header">
        <Icon type={readOnly ? 'eye' : 'edit'} size="small" />
        <Label fontWeight="semibold" value={file.title} />
        {renderSavingProgress()}
        <Tooltip value={t(mes.pagePreview)}>
          <Button
            styleName="sfj__header__btn"
            icon="panel_view"
            type={showPanel ? 'quaternary' : 'tertiary'}
            rounded={true}
            onClick={handleTogglePanel}
          />
        </Tooltip>
        {renderRefreshButton()}
      </div>
      <div styleName="sfj__content">
        {isJsonFile ? (
          <JSONEditorReact
            jsonEditorRef={editorRef}
            schema={isConfigFile ? pageConfig : {}}
            json={json}
            text=""
            mode="code"
            mainMenuBar={true}
            indentation={2}
            onChange={handleChangeJson}
            onValidationError={(err) => setErrors(err)}
            onError={() => {}}
            onEditable={() => !readOnly}
          />
        ) : (
          <AceEditorReact
            aceEditorRef={editorRef}
            mode="less"
            theme="xcode"
            text={source}
            onChange={handleChangeText}
            options={{
              useWorker: false,
              tabSize: 2,
              fontSize: 14,
              readOnly,
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: true,
              showGutter: true,
              highlightActiveLine: true,
              enableSnippets: true,
              showLineNumbers: true,
            }}
          />
        )}
      </div>
    </div>
  );
}

ScriptFileEditor.propTypes = {
  fileId: PropTypes.number,
  showPanel: PropTypes.bool,
  readOnly: PropTypes.bool,
  handleTogglePanel: PropTypes.func,
  handleChange: PropTypes.func,
};

export default ScriptFileEditor;
